"use client";

import { hasAnalyticsConsent } from "@/lib/analytics-consent";

type Primitive = string | number | boolean | null | undefined;
type AnalyticsPayload = Record<string, Primitive>;
type AnalyticsViewerContext = {
  loaded: boolean;
  isInternal: boolean;
  userRole: string | null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: "event" | "config" | "js", eventName: string | Date, params?: AnalyticsPayload) => void;
  }
}

const isBrowser = typeof window !== "undefined";
const gaId = process.env.NEXT_PUBLIC_GA_ID?.trim();
const INTERNAL_ROLES = new Set(["ADMIN", "EDITOR"]);

let viewerContext: AnalyticsViewerContext = {
  loaded: false,
  isInternal: false,
  userRole: null,
};

export const analyticsEnabled = Boolean(isBrowser && gaId);

export function isInternalAnalyticsRole(role: string | null | undefined) {
  return Boolean(role && INTERNAL_ROLES.has(role));
}

export function setAnalyticsViewerContext(context: { isInternal: boolean; userRole?: string | null }) {
  viewerContext = {
    loaded: true,
    isInternal: context.isInternal || isInternalAnalyticsRole(context.userRole),
    userRole: context.userRole ?? null,
  };
}

export function hasAnalyticsViewerContext() {
  return viewerContext.loaded;
}

export function isInternalAnalyticsViewer() {
  return viewerContext.loaded && viewerContext.isInternal;
}

export function canCaptureAnalytics() {
  return (
    analyticsEnabled &&
    hasAnalyticsConsent() &&
    typeof window.gtag === "function" &&
    viewerContext.loaded &&
    !viewerContext.isInternal
  );
}

type AnalyticsEventMap = {
  page_view: {
    page_path: string;
    page_location: string;
    page_title?: string | null;
  };
  article_open: {
    articleId: string;
    articleSlug?: string;
    articleTitle?: string;
    slug?: string;
    title?: string;
    category?: string | null;
    categorySlug?: string | null;
    categoryName?: string | null;
    authorUsername?: string | null;
    isBreaking?: boolean;
    isFeatured?: boolean;
  };
  forum_thread_open: {
    threadId: string;
    threadSlug?: string;
    threadTitle?: string;
    slug?: string;
    title?: string;
    categorySlug?: string;
    commentCount?: number;
    isMatchThread?: boolean;
    isPinned?: boolean;
    isTrending?: boolean;
  };
  thread_create: { threadId: string; categorySlug: string; isMatchThread: boolean };
  comment_create: {
    articleId?: string;
    threadId?: string;
    threadSlug?: string;
    commentId?: string;
    parentCommentId?: string | null;
    depth?: number;
  };
  comment_upvote: { commentId: string; threadId: string };
  comment_downvote: { commentId: string; threadId: string };
  notification_click: {
    notificationType: string;
    targetType?: string | null;
    targetId?: string | null;
  };
  search_performed: {
    query: string;
    resultCount?: number;
    source?: "header" | "search_page";
    hasResults?: boolean;
  };
  visszhang_open: { highlightId?: string | null };
  login: { method: "email" | "google" | string; hasUsername?: boolean; role?: string | null };
  register: { method: "email" | "google" | string; hasUsername?: boolean; role?: string | null };
  favorite_team_select: { teamSlug: string; teamName: string };
  article_share: { articleSlug: string; method: "native" | "copy_link" | "x" | "facebook" };
};

export type AnalyticsEventName = keyof AnalyticsEventMap;

function cleanPayload(payload: AnalyticsPayload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== ""),
  ) as AnalyticsPayload;
}

export function trackEvent<Name extends AnalyticsEventName>(name: Name, params?: AnalyticsEventMap[Name]) {
  captureAnalyticsEvent(name, (params ?? {}) as AnalyticsEventMap[Name]);
}

export function captureAnalyticsEvent<Name extends AnalyticsEventName>(
  eventName: Name,
  payload: AnalyticsEventMap[Name],
) {
  if (!isBrowser) return;

  const payloadRole = "role" in payload && typeof payload.role === "string" ? payload.role : null;
  if (isInternalAnalyticsRole(payloadRole)) return;

  const properties = cleanPayload({
    ...payload,
    role: undefined,
    is_internal: false,
    user_role: payloadRole && !isInternalAnalyticsRole(payloadRole) ? payloadRole : undefined,
  });

  if (process.env.NODE_ENV === "development") {
    console.debug("[analytics] captured event:", eventName, properties);
  }

  if (!canCaptureAnalytics()) return;
  window.gtag?.("event", eventName, properties);
}

export function identifyAnalyticsUser(
  userId: string,
  properties: { username: string | null; role: string | null; favoriteTeam?: string | null },
) {
  if (isInternalAnalyticsRole(properties.role)) {
    setAnalyticsViewerContext({ isInternal: true, userRole: properties.role });
    return;
  }

  setAnalyticsViewerContext({ isInternal: false, userRole: properties.role });

  if (!canCaptureAnalytics()) return;
  window.gtag?.("config", gaId ?? "", {
    user_id: userId,
    username: properties.username,
    favorite_team: properties.favoriteTeam,
    is_internal: false,
    user_role: properties.role && !isInternalAnalyticsRole(properties.role) ? properties.role : undefined,
    send_page_view: false,
  });
}

export function resetAnalyticsUser() {
  if (!isBrowser) return;
  setAnalyticsViewerContext({ isInternal: false, userRole: null });
  if (!canCaptureAnalytics()) return;
  window.gtag?.("config", gaId ?? "", { user_id: undefined, send_page_view: false });
}

// Never track sensitive values: email, auth/session tokens, passwords, comment bodies, private messages, or raw form input.
export function trackThreadOpen(payload: {
  threadId: string;
  threadSlug: string;
  threadTitle: string;
  categorySlug: string;
  commentCount: number;
  isMatchThread: boolean;
  isPinned: boolean;
  isTrending: boolean;
}) {
  captureAnalyticsEvent("forum_thread_open", payload);
}

export function trackForumThreadOpen(payload: {
  threadId: string;
  slug: string;
  title: string;
  category?: string | null;
}) {
  captureAnalyticsEvent("forum_thread_open", payload);
}

export function trackThreadCreate(payload: AnalyticsEventMap["thread_create"]) {
  captureAnalyticsEvent("thread_create", payload);
}

export function trackCommentCreate(payload: AnalyticsEventMap["comment_create"]) {
  captureAnalyticsEvent("comment_create", payload);
}

export function trackCommentUpvote(payload: AnalyticsEventMap["comment_upvote"]) {
  captureAnalyticsEvent("comment_upvote", payload);
}

export function trackCommentDownvote(payload: AnalyticsEventMap["comment_downvote"]) {
  captureAnalyticsEvent("comment_downvote", payload);
}

export function trackNotificationClick(payload: AnalyticsEventMap["notification_click"]) {
  captureAnalyticsEvent("notification_click", payload);
}

export function trackSearch(payload: AnalyticsEventMap["search_performed"] | string) {
  captureAnalyticsEvent("search_performed", typeof payload === "string" ? { query: payload } : payload);
}

export function trackArticleOpen(payload: AnalyticsEventMap["article_open"]) {
  captureAnalyticsEvent("article_open", payload);
}

export function trackVisszhangOpen(payload: AnalyticsEventMap["visszhang_open"] = {}) {
  captureAnalyticsEvent("visszhang_open", payload);
}

export function trackLogin(payload: AnalyticsEventMap["login"] | string) {
  captureAnalyticsEvent("login", typeof payload === "string" ? { method: payload } : payload);
}

export function trackRegister(payload: AnalyticsEventMap["register"] | string) {
  captureAnalyticsEvent("register", typeof payload === "string" ? { method: payload } : payload);
}

export function trackGoogleRegister(payload: Omit<AnalyticsEventMap["register"], "method"> & { method?: "google" }) {
  trackRegister({ ...payload, method: "google" });
}

export function trackGoogleLogin(payload: Omit<AnalyticsEventMap["login"], "method"> & { method?: "google" }) {
  trackLogin({ ...payload, method: "google" });
}

export function trackEmailRegister(payload: Omit<AnalyticsEventMap["register"], "method"> & { method?: "email" }) {
  trackRegister({ ...payload, method: "email" });
}

export function trackEmailLogin(payload: Omit<AnalyticsEventMap["login"], "method"> & { method?: "email" }) {
  trackLogin({ ...payload, method: "email" });
}

export function trackFavoriteTeamSelect(payload: AnalyticsEventMap["favorite_team_select"]) {
  captureAnalyticsEvent("favorite_team_select", payload);
}

export function trackArticleShare(payload: AnalyticsEventMap["article_share"]) {
  captureAnalyticsEvent("article_share", payload);
}
